import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useBlocker, type Location } from 'react-router-dom';
import { Row, Col, Card, Alert, Accordion, Modal, Button as BsButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileCode, faFilePdf, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import { useNFe } from '../../../hooks/useNFe';
import { NFeStatus, NFeTipoOperacao } from '../../../types/nfe';
import nfeService from '../../../services/nfeService';
import '../nfe.css';

// Componentes
import CabecalhoNFe from './components/CabecalhoNFe';
import SecaoCabecalho from './components/SecaoCabecalho';
import SecaoDestinatario from './components/SecaoDestinatario';
import SecaoItens from './components/SecaoItens';
import SecaoTransporte from './components/SecaoTransporte';
import SecaoDuplicatas from './components/SecaoDuplicatas';
import SecaoInformacoesAdicionais from './components/SecaoInformacoesAdicionais';
import SecaoTotais from './components/SecaoTotais';
import SecaoReferencias from './components/SecaoReferencias';
import BotaoTransmitirNFe from './components/BotaoTransmitirNFe';
import ModalCancelarNFe from './components/ModalCancelarNFe';
import BotaoCartaCorrecaoNFe from './components/BotaoCartaCorrecaoNFe';
import BotaoExcluirNFe from '../../nfe/components/BotaoExcluirNFe';

const getEditarNFeBreadcrumbItems = (numero?: number) => [
  { label: 'Operacional', url: '#!' },
  { label: 'Notas Fiscais', url: '/operacional/nfe' },
  { label: numero ? `NFe #${numero}` : 'Carregando...', active: true },
];

interface SectionDef {
  key: string;
  num: string;
  label: string;
  isComplete: (nfe: any) => boolean;
  isOptional?: boolean;
  isHidden?: (nfe: any) => boolean;
}

const SECTIONS: SectionDef[] = [
  {
    key: 'cabecalho',
    num: '1',
    label: 'Identificação',
    isComplete: (nfe) => !!(nfe.naturezaOperacao && nfe.empresaId),
  },
  {
    key: '0',
    num: '2',
    label: 'Destinatário',
    isComplete: (nfe) => !!nfe.destinatario,
  },
  {
    key: '1',
    num: '3',
    label: 'Itens',
    isComplete: (nfe) => (nfe.itens?.length ?? 0) > 0,
  },
  {
    key: '2',
    num: '4',
    label: 'Transporte',
    isComplete: (nfe) => !!nfe.transporte,
    isOptional: true,
  },
  {
    key: '3',
    num: '5',
    label: 'Duplicatas',
    isComplete: (nfe) => (nfe.duplicatas?.length ?? 0) > 0,
    isOptional: true,
  },
  {
    key: '4',
    num: '6',
    label: 'Inf. Adicionais',
    isComplete: (nfe) => !!(nfe.informacoesComplementares || nfe.informacoesFisco),
    isOptional: true,
  },
  {
    key: 'referencias',
    num: '7',
    label: 'Referências',
    isComplete: (nfe) => (nfe.referencias?.length ?? 0) > 0,
    isOptional: true,
  },
  {
    key: '5',
    num: '8',
    label: 'Totais',
    isComplete: (nfe) => (nfe.totais?.valorNota ?? 0) > 0,
  },
];

const NFeEditarSkeleton: React.FC = () => (
  <>
    <Col xs={12}>
      <div className="nfe-doc-header mb-3 placeholder-glow">
        <div className="nfe-doc-header__bg-text">NF-e</div>
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div className="flex-grow-1">
            <div className="placeholder mb-2 d-block" style={{ height: '0.65rem', width: '8rem', opacity: 0.35 }} />
            <div className="placeholder mb-2 d-block" style={{ height: '2rem', width: '15rem', opacity: 0.45 }} />
            <div className="placeholder d-block" style={{ height: '0.65rem', width: '10rem', opacity: 0.3 }} />
          </div>
          <div className="nfe-doc-header__divider d-none d-md-block" />
          <div>
            <div className="placeholder mb-1 d-block" style={{ height: '0.65rem', width: '4rem', opacity: 0.3 }} />
            <div className="placeholder d-block" style={{ height: '1rem', width: '9rem', opacity: 0.4 }} />
          </div>
          <div className="nfe-doc-header__divider d-none d-lg-block" />
          <div className="text-end" style={{ minWidth: '7rem' }}>
            <div className="placeholder mb-1 ms-auto d-block" style={{ height: '0.65rem', width: '4rem', opacity: 0.3 }} />
            <div className="placeholder mb-2 ms-auto d-block" style={{ height: '1.4rem', width: '7rem', opacity: 0.4 }} />
            <div className="placeholder ms-auto d-block" style={{ height: '1.4rem', width: '5rem', opacity: 0.3, borderRadius: '1rem' }} />
          </div>
        </div>
      </div>
    </Col>

    <Col xs={12}>
      <div className="nfe-progress placeholder-glow">
        {[4.5, 5.5, 3.5, 5, 4.5, 6, 5.5, 3.5].map((w, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="nfe-progress-sep">›</span>}
            <span className="placeholder" style={{ height: '1.75rem', width: `${w}rem`, borderRadius: '0.375rem' }} />
          </React.Fragment>
        ))}
      </div>
    </Col>

    <Col xs={12}>
      <Card className="border border-opacity-25 shadow-sm overflow-hidden">
        <div className="placeholder-glow">
          {(['7rem', '6rem', '4.5rem', '8rem', '5.5rem', '7rem', '6.5rem', '4rem'] as const).map((w, i) => (
            <div
              key={i}
              className="d-flex align-items-center gap-3 px-4 border-bottom border-opacity-25"
              style={{ height: '3.25rem' }}
            >
              <span className="placeholder rounded flex-shrink-0" style={{ width: '1.4rem', height: '1.4rem' }} />
              <span className="placeholder rounded" style={{ height: '0.8rem', width: w }} />
            </div>
          ))}
        </div>
        <Card.Footer className="border-top border-opacity-25 py-2 px-3">
          <div className="nfe-doc-footer placeholder-glow">
            <span className="placeholder rounded" style={{ height: '1.75rem', width: '8rem' }} />
            <div className="d-flex gap-2">
              <span className="placeholder rounded" style={{ height: '1.75rem', width: '4rem' }} />
              <span className="placeholder rounded" style={{ height: '1.75rem', width: '5rem' }} />
              <span className="placeholder rounded" style={{ height: '1.75rem', width: '7rem' }} />
            </div>
          </div>
        </Card.Footer>
      </Card>
    </Col>
  </>
);

const EditarNFePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const handleFocusin = (e: FocusEvent) => {
      const el = e.target as HTMLElement;
      if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'number') {
        (el as HTMLInputElement).select();
      }
    };
    document.addEventListener('focusin', handleFocusin);
    return () => document.removeEventListener('focusin', handleFocusin);
  }, []);

  const location = useLocation();
  const nfeQuery = useNFe(id);
  const isReadOnly = nfeQuery.data?.status !== NFeStatus.Rascunho && nfeQuery.data?.status !== NFeStatus.Rejeitada;

  const [activeKey, setActiveKey] = useState<string | null>(
    location.hash ? location.hash.slice(1) : null,
  );
  const [dirtySections, setDirtySections] = useState<Record<string, boolean>>({});
  const setCabecalhoDirty = useCallback((d: boolean) => setDirtySections(p => ({ ...p, cabecalho: d })), []);
  const setDestDirty = useCallback((d: boolean) => setDirtySections(p => ({ ...p, dest: d })), []);
  const setTranspDirty = useCallback((d: boolean) => setDirtySections(p => ({ ...p, transp: d })), []);
  const setInfDirty = useCallback((d: boolean) => setDirtySections(p => ({ ...p, inf: d })), []);
  const anyDirty = Object.values(dirtySections).some(Boolean);

  const blockerCondition = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) =>
      anyDirty && currentLocation.pathname !== nextLocation.pathname,
    [anyDirty],
  );
  const blocker = useBlocker(blockerCondition);

  const downloadArquivo = async (promiseFn: Promise<Blob>, fileName: string) => {
    const blob = await promiseFn;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const visibleSections = nfeQuery.data
    ? SECTIONS.filter((s) => !s.isHidden?.(nfeQuery.data))
    : SECTIONS.filter((s) => s.key !== 'referencias');

  return (
    <>
      <PageBreadcrumb items={getEditarNFeBreadcrumbItems(nfeQuery.data?.numero)} />

      <Row className="g-3">
        <Col xs={12}>
          <Button
            variant="phoenix-secondary"
            size="sm"
            onClick={() => navigate('/operacional/nfe')}
            className="d-inline-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Voltar para lista
          </Button>
        </Col>

        {nfeQuery.isLoading && <NFeEditarSkeleton />}

        {/* Error */}
        {nfeQuery.isError && (
          <Col xs={12}>
            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
              <div className="text-danger-emphasis">Erro ao carregar NF-e. Tente novamente.</div>
            </Alert>
          </Col>
        )}

        {nfeQuery.isSuccess && nfeQuery.data && (
          <>
            {/* Document header card */}
            <Col xs={12}>
              <CabecalhoNFe nfeId={id!} />
            </Col>

            {/* Rejection alert */}
            {nfeQuery.data.status === NFeStatus.Rejeitada && (nfeQuery.data.ultimaRejeicaoCstat != null || nfeQuery.data.ultimaRejeicaoXMotivo) && (
              <Col xs={12}>
                <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle py-2 mb-0">
                  <div className="text-danger-emphasis small">
                    <strong>Rejeição SEFAZ</strong>
                    {nfeQuery.data.ultimaRejeicaoCstat != null && (
                      <span className="ms-2 me-1 font-monospace">({nfeQuery.data.ultimaRejeicaoCstat})</span>
                    )}
                    {nfeQuery.data.ultimaRejeicaoXMotivo && (
                      <span>— {nfeQuery.data.ultimaRejeicaoXMotivo}</span>
                    )}
                  </div>
                </Alert>
              </Col>
            )}

            {/* Read-only alert */}
            {isReadOnly && (
              <Col xs={12}>
                <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle py-2">
                  <div className="text-info-emphasis small">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    NF-e somente leitura — status atual não permite edição.
                  </div>
                </Alert>
              </Col>
            )}

            {/* Progress indicator */}
            <Col xs={12}>
              <div className="nfe-progress">
                {visibleSections.map((s, i) => {
                  const complete = s.isComplete(nfeQuery.data);
                  return (
                    <React.Fragment key={s.key}>
                      {i > 0 && <span className="nfe-progress-sep">›</span>}
                      <button
                        type="button"
                        className={`nfe-progress-item ${complete ? 'nfe-progress-item--complete' : s.isOptional ? 'nfe-progress-item--optional' : 'nfe-progress-item--pending'}`}
                        onClick={() => setActiveKey(s.key)}
                      >
                        <span className="nfe-progress-item__num">{s.num}</span>
                        {s.label}
                        {complete && (
                          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.6rem' }} />
                        )}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </Col>

            {/* Accordion sections */}
            <Col xs={12}>
              <Card className="border border-opacity-25 shadow-sm overflow-hidden">
                <Accordion
                  activeKey={activeKey}
                  onSelect={(k) => setActiveKey(typeof k === 'string' ? k : null)}
                  className="nfe-accordion"
                >
                  {/* 1. Identificação / Emitente */}
                  <Accordion.Item eventKey="cabecalho" className={activeKey === 'cabecalho' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">1</span>
                      Identificação
                      {dirtySections['cabecalho'] && <span className="nfe-sec-dirty ms-2">● não salvo</span>}
                      {!dirtySections['cabecalho'] && nfeQuery.data.naturezaOperacao ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : !dirtySections['cabecalho'] && (
                        <span className="nfe-sec-pending ms-1">⚠ Pendente</span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoCabecalho nfeId={id!} isReadOnly={isReadOnly} onDirtyChange={setCabecalhoDirty} />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 2. Destinatário */}
                  <Accordion.Item eventKey="0" className={activeKey === '0' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">2</span>
                      Destinatário
                      {dirtySections['dest'] && <span className="nfe-sec-dirty ms-2">● não salvo</span>}
                      {!dirtySections['dest'] && nfeQuery.data.destinatario ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : !dirtySections['dest'] && (
                        <span className="nfe-sec-pending ms-1">⚠ Pendente</span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoDestinatario
                        nfeId={nfeQuery.data.emissaoId}
                        isReadOnly={isReadOnly}
                        onDirtyChange={setDestDirty}
                      />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 3. Itens */}
                  <Accordion.Item eventKey="1" className={activeKey === '1' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">3</span>
                      Itens
                      <span className="text-body-secondary fw-normal ms-2" style={{ fontSize: '0.75rem' }}>
                        ({nfeQuery.data.itens?.length ?? 0})
                      </span>
                      {(nfeQuery.data.itens?.length ?? 0) > 0 ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : (
                        <span className="nfe-sec-pending ms-1">⚠ Pendente</span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoItens
                        nfeId={nfeQuery.data.emissaoId}
                        isReadOnly={isReadOnly}
                      />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 4. Transporte */}
                  <Accordion.Item eventKey="2" className={activeKey === '2' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">4</span>
                      Transporte
                      {dirtySections['transp'] && <span className="nfe-sec-dirty ms-2">● não salvo</span>}
                      {!dirtySections['transp'] && nfeQuery.data.transporte ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : !dirtySections['transp'] && (
                        <span className="text-body-tertiary fw-normal ms-2" style={{ fontSize: '0.72rem' }}>
                          opcional
                        </span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoTransporte
                        nfeId={nfeQuery.data.emissaoId}
                        isReadOnly={isReadOnly}
                        onDirtyChange={setTranspDirty}
                      />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 5. Duplicatas */}
                  <Accordion.Item eventKey="3" className={activeKey === '3' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">5</span>
                      Duplicatas
                      <span className="text-body-secondary fw-normal ms-2" style={{ fontSize: '0.75rem' }}>
                        ({nfeQuery.data.duplicatas?.length ?? 0} parcelas)
                      </span>
                      {(nfeQuery.data.duplicatas?.length ?? 0) > 0 ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : (
                        <span className="text-body-tertiary fw-normal ms-2" style={{ fontSize: '0.72rem' }}>
                          opcional
                        </span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoDuplicatas
                        nfeId={nfeQuery.data.emissaoId}
                        isReadOnly={isReadOnly}
                      />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 6. Informações Adicionais */}
                  <Accordion.Item eventKey="4" className={activeKey === '4' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">6</span>
                      Informações Adicionais
                      {dirtySections['inf'] && <span className="nfe-sec-dirty ms-2">● não salvo</span>}
                      {!dirtySections['inf'] && (nfeQuery.data.informacoesComplementares || nfeQuery.data.informacoesFisco) ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : !dirtySections['inf'] && (
                        <span className="text-body-tertiary fw-normal ms-2" style={{ fontSize: '0.72rem' }}>
                          opcional
                        </span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoInformacoesAdicionais
                        nfeId={nfeQuery.data.emissaoId}
                        isReadOnly={isReadOnly}
                        onDirtyChange={setInfDirty}
                      />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 7. Referências */}
                  <Accordion.Item eventKey="referencias" className={activeKey === 'referencias' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">7</span>
                      Referências NF-e
                      <span className="text-body-secondary fw-normal ms-2" style={{ fontSize: '0.75rem' }}>
                        ({nfeQuery.data.referencias?.length ?? 0})
                      </span>
                      {(nfeQuery.data.referencias?.length ?? 0) > 0 ? (
                        <span className="nfe-sec-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                      ) : (
                        <span className="text-body-tertiary fw-normal ms-2" style={{ fontSize: '0.72rem' }}>
                          opcional
                        </span>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoReferencias
                        emissaoId={id!}
                        isReadOnly={isReadOnly}
                      />
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* 8. Totais */}
                  <Accordion.Item eventKey="5" className={activeKey === '5' ? 'active-section' : ''}>
                    <Accordion.Header>
                      <span className="nfe-sec-num">8</span>
                      Totais
                      <span className="nfe-mono ms-2 text-body-secondary fw-normal" style={{ fontSize: '0.8rem' }}>
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                          nfeQuery.data.totais?.valorNota ?? 0
                        )}
                      </span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <SecaoTotais nfeId={nfeQuery.data.emissaoId} />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                {/* Footer actions */}
                <Card.Footer className="p-0 border-top border-opacity-25">
                  <div className="nfe-doc-footer">
                    <div className="nfe-doc-footer__left">
                      {!isReadOnly && (
                        <BotaoExcluirNFe
                          emissaoId={id!}
                          nfeNumero={nfeQuery.data.numero}
                          redirectAfterDelete
                        />
                      )}
                    </div>

                    <div className="nfe-doc-footer__right">
                      {(nfeQuery.data.status === NFeStatus.Autorizada ||
                        nfeQuery.data.status === NFeStatus.Cancelada) && (
                        <Button
                          variant="phoenix-secondary"
                          size="sm"
                          onClick={() =>
                            downloadArquivo(nfeService.downloadXmlNFe(id!), `nfe_${nfeQuery.data.chave ?? id}.xml`)
                          }
                        >
                          <FontAwesomeIcon icon={faFileCode} className="me-1" />
                          XML
                        </Button>
                      )}

                      <Button
                        variant="phoenix-secondary"
                        size="sm"
                        onClick={() => window.open(`/api/nfe/${nfeQuery.data!.nFeId}/danfe`, '_blank')}
                      >
                        <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                        DANFE
                      </Button>

                      {(nfeQuery.data.status === NFeStatus.Rascunho ||
                        nfeQuery.data.status === NFeStatus.Completa ||
                        nfeQuery.data.status === NFeStatus.Rejeitada) && (
                        <BotaoTransmitirNFe emissaoId={id!} />
                      )}

                      {nfeQuery.data.status === NFeStatus.Autorizada && (
                        <BotaoCartaCorrecaoNFe emissaoId={id!} />
                      )}

                      {nfeQuery.data.status === NFeStatus.Autorizada && (
                        <ModalCancelarNFe emissaoId={id!} dhRecbto={nfeQuery.data.dhRecbto} />
                      )}
                    </div>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Modal show={blocker.state === 'blocked'} onHide={() => blocker.reset?.()}>
        <Modal.Header closeButton>
          <Modal.Title>Alterações não salvas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Você tem alterações não salvas em uma ou mais seções. Deseja sair mesmo assim?
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" onClick={() => blocker.reset?.()}>
            Continuar editando
          </BsButton>
          <BsButton variant="danger" onClick={() => blocker.proceed?.()}>
            Sair sem salvar
          </BsButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditarNFePage;
